import { AllComponents } from "../../../../components/_default/interfaces/base";
import type { Editor } from "./editorTypes";
import { getComponentFromStructureMap } from "../../../../components/_default/utilities/componentIdentity";

/** DOM id for builder component wrappers: prefer UUID, then numeric id. */
export function getComponentDomId(
  component: Pick<AllComponents, "id" | "uuid"> | null | undefined
): string {
  if (!component) {
    return "";
  }
  if (component.uuid && component.uuid.trim() !== "") {
    return component.uuid;
  }
  return String(component.id);
}

/**
 * Root `.builder-component` element for toolbar positioning and hit-testing.
 * Tries UUID id first (when canvas uses uuid `id`), then legacy numeric `id`
 * (still used by most default components until step 3.7).
 */
export function getBuilderComponentRootElement(
  component: Pick<AllComponents, "id" | "uuid"> | null | undefined
): HTMLElement | null {
  if (!component) {
    return null;
  }
  const preferred = getComponentDomId(component);
  let el = document.getElementById(preferred);
  if (el) {
    return el;
  }
  const legacy = String(component.id);
  if (legacy !== preferred) {
    el = document.getElementById(legacy);
  }
  return el;
}

/**
 * Live component from `structure.components` when the map may be keyed by UUID
 * or legacy numeric id (same identity as {@link getBuilderComponentRootElement}).
 */
export function getStructureComponentByIdentity(
  components: { [key: string]: AllComponents } | undefined,
  component:
    | Pick<AllComponents, "id" | "uuid">
    | null
    | undefined
    | false
): AllComponents | undefined {
  if (!components || component === null || component === undefined || component === false) {
    return undefined;
  }
  if (component.uuid && component.uuid.trim() !== "") {
    const byUuid = components[component.uuid];
    if (byUuid) {
      return byUuid;
    }
  }
  const byId = components[String(component.id)];
  if (byId) {
    return byId;
  }
  return undefined;
}

/** Map key for `structure.components` from editor state (UUID-first). */
export function getEditorComponentMapKey(editor: Editor): string | null {
  if (editor.component_uuid && editor.component_uuid.trim() !== "") {
    return editor.component_uuid;
  }
  if (editor.component_id != null && editor.component_id !== -11) {
    return String(editor.component_id);
  }
  return null;
}

export function getEditorParentComponentMapKey(editor: Editor): string | null {
  if (editor.component_parent_uuid && editor.component_parent_uuid.trim() !== "") {
    return editor.component_parent_uuid;
  }
  if (editor.component_parent_id != null) {
    return String(editor.component_parent_id);
  }
  return null;
}

/**
 * Resolves the key used in `structure.components` for the current editor selection.
 * Tries UUID first, then legacy numeric id string.
 */
export function resolveEditorComponentStructureKey(
  components: { [key: string]: AllComponents } | undefined,
  editor: Editor
): string | null {
  if (!components) {
    return null;
  }
  const primary = getEditorComponentMapKey(editor);
  if (primary && components[primary]) {
    return primary;
  }
  if (editor.component_id != null && editor.component_id !== -11) {
    const legacy = String(editor.component_id);
    if (components[legacy]) {
      return legacy;
    }
  }
  return null;
}

/** Parent row in `structure.components` from a child's `parent_id` / `parent_uuid`. */
export function resolveParentComponentFromChild(
  components: { [key: string]: AllComponents } | undefined,
  component: AllComponents
): AllComponents | undefined {
  if (!components) {
    return undefined;
  }
  const parentUuidRaw = (component as { parent_uuid?: string | null })
    .parent_uuid;
  const parentUuid =
    typeof parentUuidRaw === "string" ? parentUuidRaw.trim() : "";
  const parentById =
    component.parent_id != null && component.parent_id !== undefined
      ? getComponentFromStructureMap(components, component.parent_id)
      : undefined;
  const parentByUuid =
    !parentById && parentUuid
      ? getComponentFromStructureMap(components, parentUuid)
      : undefined;
  return parentById ?? parentByUuid;
}

/** Editor id fields from a component and optional parent (for selection updates). */
export function editorIdsFromComponent(
  component: AllComponents,
  components: { [key: string]: AllComponents } | undefined
): Pick<Editor, "component_uuid" | "component_parent_uuid" | "component_id" | "component_parent_id"> {
  const resolvedParent = resolveParentComponentFromChild(components, component);
  return {
    component_uuid:
      component.uuid && component.uuid.trim() !== "" ? component.uuid : null,
    component_parent_uuid:
      resolvedParent?.uuid && resolvedParent.uuid.trim() !== ""
        ? resolvedParent.uuid
        : null,
    component_id: component.id,
    component_parent_id: component.parent_id ?? null,
  };
}
